import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../shared/components/error-messages/error-messages.component';
import { FormUtilitiesService } from '../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { KanbanViewColumnsDialogComponent } from './kanban-view-columns-dialog/kanban-view-columns-dialog.component';
import { IKanbanView, TAddKanbanView, TFormControlKanbanView, TFormKanbanView } from './kanban-views.interface';
import { IKanbanViewComponentState, KanbanViewsStore } from './kanban-views.store';
import { NgIf, UpperCasePipe } from '@angular/common';

@Component({
  imports: [
    DatatableComponent,
    ErrorMessagesComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatChip,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslateModule,
    UpperCasePipe,
    NgIf,
  ],
  providers: [KanbanViewsStore, HttpUtilitiesService],
  selector: 'app-kanban-views',
  standalone: true,
  templateUrl: './kanban-views.component.html',
})
export class KanbanViewsComponent
  extends BaseCrudPageComponent<IKanbanView, IKanbanViewComponentState>
  implements OnInit
{
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;

  public datatableConfigurations: IDatatableConfiguration<IKanbanView> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.single,
    // The seeded default view is immutable: disabling its checkbox prevents it from being
    // selected, which is what the edit/delete actions operate on.
    disableCheckboxCondition: (row: IKanbanView) => row.isDefault,
    editScope: ETableCrudScope.single,
  };

  public readonly datatableColumns: IDatatableColumn<IKanbanView>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
  ];

  public readonly addEditForm: FormGroup<TFormControlKanbanView> = new FormGroup({
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)]),
  });

  constructor(
    public readonly store: KanbanViewsStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfKanbanView',
      multiContext: 'field.kanbanViews',
      nameProperty: 'name',
      singleContext: 'field.kanbanView',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.datatableParams$.pipe(filter(Boolean)).subscribe((datatable) => {
        this.store.loadItems({
          fields: ['name', 'isDefault'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();
    this.addEditForm.reset({ name: '' });

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public initializeEdit(): void {
    const [item] = this.selectedRows();

    if (!item) {
      return;
    }

    this.setupForEdit();
    this.addEditForm.enable();
    this.addEditForm.reset({ name: item.name });

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    const payload: TAddKanbanView = { name: FormUtilitiesService.trimIfString((this.addEditForm.value as TFormKanbanView).name) };

    if (!this.isEditDialog()) {
      this.store.createOne(payload);
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({ dto: payload, id: item.id });
    }

    this.matDialog.closeAll();
  }

  public displayName(row: IKanbanView): string {
    return row.isDefault ? this.translate.instant('page.kanbanConfig.defaultViewName') : row.name;
  }

  public openColumns(row: IKanbanView): void {
    this.matDialog.open(KanbanViewColumnsDialogComponent, {
      ...KanbanViewColumnsDialogComponent.dialogParameters,
      data: { kanbanViewId: row.id, kanbanViewName: this.displayName(row), readOnly: row.isDefault },
    });
  }
}
